import { ChangeDetectorRef, Component, HostListener, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ToastrService } from "ngx-toastr";
import { ApiService } from "../services";
import { LoadingScreenService } from "../shared/loading-screen.service";
import { GridViewBaseComponent } from "../utility/grid-view-base.component";
import { SharedDataService } from "../services/sharedDataService";
import { extendDefaultOptions, agDateColumnFilter } from "../utility/gridHelper";
import { PreprocessDateTime } from "../utility/view-field-mapping";
import { LinkCellComponent } from "../shared/grid/link-cell.component";
import { Demand } from "../generated-models/Demand";
import { DemandItem } from "../generated-models/DemandItem";
import { DemandItemAlternative } from "../generated-models/DemandItemAlternative";
import { DemandItemExpanded } from "../generated-models/DemandItemExpanded";
import {
  AlternativeSelectionModalComponent,
  AlternativeSelectionData,
  ALTERNATIVE_SELECTION_CLEARED,
} from "./alternative-selection-modal/alternative-selection-modal.component";

// Per-DemandItem chosen alternative (Order=0 means original).
interface ChosenAlternative {
  order: number;
  sku: string;
  reserve: number;
  limit: number;
  reserveFormula?: string;
  limitFormula?: string;
  isOriginal: boolean;
}

interface DemandSignoff {
  Type: string;
  Status: string;
  By: string | null;
  At: string | null;
}

@Component({
  standalone: false,
  templateUrl: "./demand-details.component.html",
  styleUrls: ["../styles.scss", "./capacity-blueprint.scss"],
})
export class CapacityBlueprintDemandDetailsComponent extends GridViewBaseComponent implements OnInit {
  demandId!: number;
  /** History fetched during `resolveDemandId` and reused in `loadAllData`. */
  private cachedHistory: Demand[] | null = null;
  private hasResolvedDemand = false;
  private isLoadingData = false;
  initPageSize = 10;
  keyFilterStats: string = "filter:capacity-blueprint-demand-details";

  // Required by GridViewBaseComponent
  gridOptions!: GridOptions;
  gridApi!: GridApi;

  // Grid APIs
  demandItemGridApi!: GridApi;
  demandItemExpandedGridApi!: GridApi;
  demandHistoryGridApi!: GridApi;

  // Grid Options
  demandItemGridOptions!: GridOptions<DemandItem>;
  demandItemExpandedGridOptions!: GridOptions<DemandItemExpanded>;
  demandHistoryGridOptions!: GridOptions<Demand>;

  // Data
  demand: Demand | null = null;
  processedCreatedTime: string | null = null;
  isRefreshing = false;
  isAdmin = false;

  /**
   *  True when the demand is no longer in a Created state and therefore alternatives
   *  can no longer be changed. Non-admins may still modify alternatives while the
   *  demand is Created — only Sign Off / Reject are admin-only (see `canSignOrReject`).
   */
  get isReadOnly(): boolean {
    return ((this.demand?.Status ?? "").toLowerCase() !== "created");
  }

  /** Sign Off / Reject buttons require an admin AND a Created demand. */
  get canSignoffOrReject(): boolean {
    return this.isAdmin && !this.isReadOnly;
  }

  /** True when the demand has been marked Removed; hide all action buttons. */
  get isArchived(): boolean {
    return ((this.demand?.Status ?? "").toLowerCase() === "archived");
  }

  get statusBadgeClass(): string {
    switch ((this.demand?.Status ?? "").toLowerCase()) {
      case "pending":
      case "created":
        return "bg-warning text-dark";
      case "signedoff":
        return "bg-success";
      case "rejected":
        return "bg-danger";
      default:
        return "bg-secondary";
    }
  }

  get getDemandStatus(): string {
    return this.demand?.Status ?? "";
  }

  get isFirstPartyCustomer(): boolean {
    return this.demand?.CustomerType === '1P';
  }

  demandItemRowData: DemandItem[] = [];
  demandItemExpandedRowData: DemandItemExpanded[] = [];
  demandSignoffRowData: DemandSignoff[] = [];
  demandHistoryRowData: Demand[] = [];

  // Per DemandItem.Id -> chosen alternative (Order=0 means original).
  chosenAlternatives = new Map<number, ChosenAlternative>();

  constructor(
    protected apiService: ApiService,
    private loadingService: LoadingScreenService,
    protected sharedDataService: SharedDataService,
    private notificationService: ToastrService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private modalService: NgbModal,
    private titleService: Title,
  ) {
    super(apiService, sharedDataService);
  }

  async ngOnInit(): Promise<void> {
    this.apiService
      .getUserProfile()
      .then((profile) => {
        this.isAdmin = !!profile?.IsAdmin;
      })
      .catch(() => {
        this.isAdmin = false;
      });
    this.initializeAllGrids();
    const resolved = await this.resolveDemandId();
    if (!resolved) {
      return;
    }
    this.hasResolvedDemand = true;
    this.titleService.setTitle(`Demand ${this.demandId} - Capacity Blueprint`);
    await this.loadAllData();
  }

  /**
   *  Populates {@link demandId} and {@link highlightId} from either the ID route
   *  (`/capacity/demand/:demandId`) or the logical-key route
   *  (`/capacity/demand/serviceTreeId/.../type/.../name/.../region/...?ID=<n>`).
   *  For the logical-key route, resolves the numeric demand id from the latest
   *  history version when `?ID` is not supplied.
   */
  private async resolveDemandId(): Promise<boolean> {
    const paramMap = this.route.snapshot.paramMap;
    const queryMap = this.route.snapshot.queryParamMap;

    let serviceTreeId: string | null;
    let type: string | null;
    let name: string | null;
    let region: string | null;

    const demandIdParam = paramMap.get("demandId");
    if (demandIdParam) {
      const parsed = Number(demandIdParam);
      if (Number.isNaN(parsed)) {
        this.notificationService.error(`Invalid demand id: '${demandIdParam}'.`);
        return false;
      }
      this.demandId = parsed;

      try {
        this.demand = await this.apiService.getDemand(this.demandId).toPromise() ?? null;
        if (!this.demand) {
          this.notificationService.error(`Demand with id '${this.demandId}' was not found.`);
          return false;
        }
        serviceTreeId = this.demand.ServiceTreeId ?? null;
        type = this.demand.Type ?? null;
        name = this.demand.BlueprintName ?? null;
        region = this.demand.RegionName ?? null;
        if (!serviceTreeId || !type || !name || !region) {
          return true;
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        this.notificationService.error(`Failed to resolve demand: ${message}`);
        return false;
      }
    } else {
      serviceTreeId = paramMap.get("serviceTreeId");
      type = paramMap.get("type");
      name = paramMap.get("name");
      region = paramMap.get("region");
      if (!serviceTreeId || !type || !name || !region) {
        this.notificationService.error("Missing route parameters for demand details.");
        return false;
      }

      const queryId = queryMap.get("ID");
      if (queryId != null) {
        const parsed = Number(queryId);
        if (Number.isNaN(parsed)) {
          this.notificationService.error(`Invalid demand id: '${queryId}'.`);
          return false;
        }
        this.demandId = parsed;
        this.demand = await this.apiService.getDemand(this.demandId).toPromise() ?? null;
        if (!this.demand) {
          this.notificationService.error(`Demand with id '${this.demandId}' was not found.`);
          return false;
        }
      }
    }

    try {
      const result = await this.apiService
        .getDemandHistory(serviceTreeId, type, name, region)
        .toPromise();
      const history = result ?? [];
      this.cachedHistory = history;

      if (!this.demand) {
        const latestDemand = history[0];
        if (!latestDemand) {
          this.notificationService.error("No demand was found for the supplied logical key.");
          return false;
        }
        this.demand = latestDemand;
        this.demandId = latestDemand.Id;
      }
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      this.notificationService.error(`Failed to resolve demand: ${message}`);
      return false;
    }
  }
  
  // ---------- Grid initialization ----------

  private initializeAllGrids(): void {
    const highlightCellClass = "highlighted-cell";
    const highlightHeaderClass = "highlighted-header";

    const demandItemColumns: ColDef<DemandItem>[] = [
      { headerName: "Type", field: "Type", filter: "agTextColumnFilter", width: 100 },
      {
        headerName: "Name",
        field: "Name",
        filter: "agTextColumnFilter",
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          target: "_blank",
          text: (params: ICellRendererParams) => (params.data as DemandItem)?.Name ?? "",
          url: (params: ICellRendererParams) => this.buildDemandItemDetailsUrl(params.data as DemandItem),
        },
      },
      {
        headerName: "Choose Alternative",
        colId: "ChooseAlternative",
        width: 180,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams) => this.renderChooseAlternativeCell(params),
      },
      {
        headerName: "SKU",
        field: "SKU",
        filter: "agTextColumnFilter",
        cellClass: highlightCellClass,
        headerClass: highlightHeaderClass,
        valueGetter: (p: ValueGetterParams) => this.effectiveSku(p.data as DemandItem),
      },
      {
        headerName: "Reserve Formula",
        field: "ReserveFormula",
        filter: "agTextColumnFilter",
        width: 140,
        cellClass: highlightCellClass,
        headerClass: highlightHeaderClass,
        valueGetter: (p: ValueGetterParams) => this.effectiveReserveFormula(p.data as DemandItem),
      },
      {
        headerName: "Reserve",
        field: "ReserveCalculated",
        filter: "agNumberColumnFilter",
        width: 100,
        cellClass: highlightCellClass,
        headerClass: highlightHeaderClass,
        valueGetter: (p: ValueGetterParams) => this.effectiveReserve(p.data as DemandItem),
      },
      {
        headerName: "Limit Formula",
        field: "LimitFormula",
        filter: "agTextColumnFilter",
        width: 140,
        cellClass: highlightCellClass,
        headerClass: highlightHeaderClass,
        valueGetter: (p: ValueGetterParams) => this.effectiveLimitFormula(p.data as DemandItem),
      },
      {
        headerName: "Limit",
        field: "LimitCalculated",
        filter: "agNumberColumnFilter",
        width: 90,
        cellClass: highlightCellClass,
        headerClass: highlightHeaderClass,
        valueGetter: (p: ValueGetterParams) => this.effectiveLimit(p.data as DemandItem),
      },
      {
        headerName: "Zone",
        field: "Zone",
        filter: "agTextColumnFilter",
        width: 90,
        valueGetter: (p: ValueGetterParams) => this.displayZone((p.data as DemandItem)?.Zone),
      },
      {
        headerName: "Cluster Count",
        field: "ClusterCount",
        filter: "agNumberColumnFilter",
        width: 120,
        valueGetter: (p: ValueGetterParams) => this.displayPlacementCount((p.data as DemandItem)?.ClusterCount),
      },
      {
        headerName: "Cluster Distribution",
        field: "ClusterDistribution",
        filter: "agTextColumnFilter",
        width: 150,
      },
      {
        headerName: "Rack Count",
        field: "RackCount",
        filter: "agNumberColumnFilter",
        width: 110,
        valueGetter: (p: ValueGetterParams) => this.displayPlacementCount((p.data as DemandItem)?.RackCount),
      },
      {
        headerName: "Rack Distribution",
        field: "RackDistribution",
        filter: "agTextColumnFilter",
        width: 140,
      },
      {
        headerName: "Start Date",
        field: "StartDate",
        cellRenderer: (params: ICellRendererParams) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        width: 110,
      },
      {
        headerName: "End Date",
        field: "EndDate",
        cellRenderer: (params: ICellRendererParams) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        width: 110,
      },
      {
        headerName: "Properties",
        colId: "Properties",
        filter: "agTextColumnFilter",
        minWidth: 200,
        autoHeight: true,
        wrapText: true,
        valueGetter: (p: ValueGetterParams) => this.formatProperties((p.data as DemandItem)?.Properties),
      },
    ];

    const demandItemExpandedColumns: ColDef<DemandItemExpanded>[] = [
      { headerName: "Type", field: "Type", filter: "agTextColumnFilter", width: 100 },
      { headerName: "Name", field: "Name", filter: "agTextColumnFilter" },
      {
        headerName: "SKU",
        field: "SKU",
        filter: "agTextColumnFilter",
        valueGetter: (p: ValueGetterParams) => this.effectiveExpandedSku(p.data as DemandItemExpanded),
      },
      {
        headerName: "Reserve",
        field: "ReserveCalculated",
        filter: "agNumberColumnFilter",
        width: 100,
        valueGetter: (p: ValueGetterParams) => this.effectiveExpandedReserve(p.data as DemandItemExpanded),
      },
      {
        headerName: "Limit",
        field: "LimitCalculated",
        filter: "agNumberColumnFilter",
        width: 90,
        valueGetter: (p: ValueGetterParams) => this.effectiveExpandedLimit(p.data as DemandItemExpanded),
      },
      {
        headerName: "Zone",
        field: "Zone",
        filter: "agTextColumnFilter",
        width: 90,
        valueGetter: (p: ValueGetterParams) => this.displayZone((p.data as DemandItemExpanded)?.Zone),
      },
      {
        headerName: "Start Date",
        field: "StartDate",
        cellRenderer: (params: ICellRendererParams) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        width: 110,
      },
      {
        headerName: "End Date",
        field: "EndDate",
        cellRenderer: (params: ICellRendererParams) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        width: 110,
      },
    ];

    this.demandItemGridOptions = extendDefaultOptions({
      columnDefs: demandItemColumns,
      paginationPageSize: this.initPageSize,
      animateRows: true,
    });

    this.demandItemExpandedGridOptions = extendDefaultOptions({
      columnDefs: demandItemExpandedColumns,
      paginationPageSize: this.initPageSize,
      animateRows: true,
    });

    this.demandHistoryGridOptions = extendDefaultOptions({
      columnDefs: this.buildDemandHistoryColumns(),
      paginationPageSize: this.initPageSize,
      rowSelection: "single",
      animateRows: true,
      getRowClass: (params) => ((params.data as { Id?: number })?.Id === this.demandId ? "current-version-row" : ""),
    });

    // Required by GridViewBaseComponent.
    this.gridOptions = this.demandItemGridOptions;
  }

  private buildDemandHistoryColumns(): ColDef[] {
    return [
      {
        headerName: "Id",
        field: "Id",
        filter: "agNumberColumnFilter",
        width: 100,
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          commands: (params: any) =>
            params.data?.Id != null ? ["/capacity/demand", params.data.Id] : null,
          target: "_blank",
        },
      },
      {
        headerName: "Blueprint Id",
        field: "BlueprintId",
        filter: "agNumberColumnFilter",
        cellRenderer: LinkCellComponent,
        cellRendererParams: {
          url: (params: { data?: Demand }) => {
            if (!params.data) {
              return null;
            }
            const { BlueprintId, ServiceTreeId, Type, BlueprintName } = params.data;
            console.log(params.data);
            if (BlueprintId == null || !ServiceTreeId || !Type || !BlueprintName) {
              return null;
            }
            return `/quota/capacity/blueprint/serviceTreeId/${encodeURIComponent(ServiceTreeId)}/type/${encodeURIComponent(Type)}/name/${encodeURIComponent(BlueprintName)}?ID=${BlueprintId}`;
          },
          target: "_blank",
        },
      },
      { headerName: "Status", field: "Status", filter: "agTextColumnFilter" },
      { headerName: "Change Reason", field: "ChangeReason", filter: "agTextColumnFilter" },
      { headerName: "Customer Contact", field: "CustomerContact", filter: "agTextColumnFilter" },
      { headerName: "Business Priority", field: "BusinessPriority", filter: "agTextColumnFilter" },
      { headerName: "Last Updated By", field: "LastUpdatedBy", filter: "agTextColumnFilter" },
      {
        headerName: "Created Time (UTC)",
        field: "CreatedTime",
        cellRenderer: (params: any) => PreprocessDateTime(params.value),
        filter: "agDateColumnFilter",
        filterParams: {
          comparator: (a: any, b: any) => agDateColumnFilter(a, b),
        },
      },
    ];
  }

  private renderChooseAlternativeCell(params: ICellRendererParams): HTMLElement | string {
    const item = params.data as DemandItem;
    if (!item || !this.itemHasAlternatives(item.Id)) {
      return "";
    }

    const wrapper = document.createElement("div");
    wrapper.style.display = "flex";
    wrapper.style.alignItems = "center";
    wrapper.style.gap = "6px";

    const chosen = this.chosenAlternatives.get(item.Id);

    // Lock the cell only when the demand itself is no longer Created. Per-type
    // signoff status is intentionally ignored here — DemandItem.Type and
    // DemandSignoff.Type are different concepts, so keying off pending signoffs
    // wrongly locked items as "Original" even while the demand was still pending.
    if (this.isReadOnly) {
      const label = document.createElement("span");
      label.style.fontSize = "12px";
      if (chosen) {
        label.style.color = chosen.isOriginal ? "#555" : "#0078d4";
        label.innerText = chosen.isOriginal ? "Original" : `Alt ${chosen.order}`;
      } else {
        label.style.color = "#555";
        label.innerText = "Original";
      }
      wrapper.appendChild(label);
      return wrapper;
    }

    const link = document.createElement("a");
    link.href = "javascript:void(0)";
    link.className = "grid-link";
    link.innerText = chosen ? "Change" : "Choose";
    link.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openAlternativeModal(item);
    });
    wrapper.appendChild(link);

    if (chosen) {
      const label = document.createElement("span");
      label.style.fontSize = "12px";
      label.style.color = chosen.isOriginal ? "#555" : "#0078d4";
      label.innerText = chosen.isOriginal ? "(Original)" : `(Alt ${chosen.order})`;
      wrapper.appendChild(label);
    } else {
      const warn = document.createElement("span");
      warn.style.fontSize = "12px";
      warn.style.color = "#a94442";
      warn.innerText = "(Origial)";
      wrapper.appendChild(warn);
    }

    return wrapper;
  }

  // ---------- Grid ready handlers ----------

  onDemandItemGridReady(params: GridReadyEvent): void {
    this.demandItemGridApi = params.api;
    params.api.autoSizeAllColumns();
  }

  onDemandItemExpandedGridReady(params: GridReadyEvent): void {
    this.demandItemExpandedGridApi = params.api;
    params.api.autoSizeAllColumns();
  }

  onDemandHistoryGridReady(params: GridReadyEvent): void {
    this.demandHistoryGridApi = params.api;
    params.api.autoSizeAllColumns();
  }

  // ---------- Data loading ----------

  private async loadAllData(): Promise<void> {
    if (!this.hasResolvedDemand || this.isLoadingData) {
      return;
    }

    this.isLoadingData = true;
    this.loadingService.setLoading(true);
    try {
      const [items, signoffs] = await Promise.all([
        this.apiService.getDemandItemsByDemandId(this.demandId).toPromise(),
        this.apiService.getDemandSignoffsByDemandId(this.demandId).toPromise(),
      ]);

      this.processedCreatedTime = this.demand?.CreatedTime ? PreprocessDateTime(this.demand.CreatedTime) : null;
      this.demandItemRowData = (items ?? []) as DemandItem[];
      this.demandItemExpandedRowData = this.flattenExpandedItems(this.demandItemRowData);
      this.demandSignoffRowData = (signoffs ?? []) as DemandSignoff[];
      this.demandHistoryRowData = this.cachedHistory ?? [];

      // Reset selection state on (re)load and rebuild pending types set.
      this.chosenAlternatives.clear();

      // Rehydrate previously assigned alternatives so already-signed-off rows
      // continue to display the chosen alternative's SKU/Reserve/Limit instead
      // of falling back to the original (the backend persists the picked Order
      // in DemandItem.AssignedAlternative but does not overwrite the row).
      this.hydrateAssignedAlternatives();

      setTimeout(() => {
        this.demandItemGridApi?.setGridOption("rowData", this.demandItemRowData);
        this.demandItemGridApi?.autoSizeAllColumns();
        this.demandItemExpandedGridApi?.setGridOption("rowData", this.demandItemExpandedRowData);
        this.demandItemExpandedGridApi?.autoSizeAllColumns();
        this.demandHistoryGridApi?.setGridOption("rowData", this.demandHistoryRowData);
        this.demandHistoryGridApi?.autoSizeAllColumns();
      }, 100);
    } catch (error: any) {
      this.notificationService.error(`Failed to load data: ${error?.message || error}`);
    } finally {
      this.isLoadingData = false;
      this.loadingService.setLoading(false);
    }
  }

  /** Deep link to the demand-item details page using its logical key and version id. */
  private buildDemandItemDetailsUrl(item: DemandItem | null | undefined): string | null {
    if (!item || item.Id == null || !item.Name || !this.demand) {
      return null;
    }
    const { ServiceTreeId, Type, BlueprintName, RegionName } = this.demand;
    if (!ServiceTreeId || !Type || !BlueprintName || !RegionName) {
      return null;
    }
    return `/quota/capacity/demand-item/serviceTreeId/${encodeURIComponent(ServiceTreeId)}/type/${encodeURIComponent(Type)}/name/${encodeURIComponent(BlueprintName)}/region/${encodeURIComponent(RegionName)}/resource/${encodeURIComponent(item.Name)}?ID=${item.Id}`;
  }

  refreshData(): void {
    this.loadAllData();
  }

  /**
   *  Calls the server-side refresh endpoint to re-evaluate demand items and
   *  related tables, then reloads the page data.
   */
  async refreshDemandItems(): Promise<void> {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    try {
      this.loadingService.setLoading(true);
      await this.apiService.refreshDemandItems(this.demandId).toPromise();
      await this.loadAllData();
      this.notificationService.info("Refresh demand items successfully!");
    } catch (error: any) {
      this.notificationService.error(`Failed to refresh: ${error?.message || error}`);
    } finally {
      this.isRefreshing = false;
      this.loadingService.setLoading(false);
    }
  }

  // ---------- Helpers ----------
  private itemHasAlternatives(demandItemId: number): boolean {
    return this.alternativesForItem(demandItemId).length > 0;
  }

  private alternativesForItem(demandItemId: number): DemandItemAlternative[] {
    const item = this.demandItemRowData.find((i) => i.Id === demandItemId);
    return item?.Alternative ?? [];
  }

  private flattenExpandedItems(items: DemandItem[]): DemandItemExpanded[] {
    const result: DemandItemExpanded[] = [];
    for (const item of items) {
      if (item?.ExpandedItems?.length) {
        result.push(...item.ExpandedItems);
      }
    }
    return result;
  }

  private formatProperties(properties: { Key: string; Value: string }[] | undefined | null): string {
    if (!properties || properties.length === 0) return "";
    return properties.map((p) => `${p.Key}: ${p.Value}`).join("; ");
  }

  /**
   *  Walks the loaded DemandItems and, for any row whose AssignedAlternative
   *  identifies a non-zero alternative order, populates `chosenAlternatives`
   *  so the displayed SKU/Reserve/Limit reflect the persisted choice.
   */
  private hydrateAssignedAlternatives(): void {
    for (const item of this.demandItemRowData) {
      const orderRaw = (item as any).AssignedAlternative;
      const order = Number(orderRaw);
      if (!orderRaw || isNaN(order) || order <= 0) {
        continue;
      }

      const alternatives = this.alternativesForItem(item.Id);
      const match = alternatives.find((a) => Number(a.Order) === order);
      if (!match) {
        continue;
      }

      this.chosenAlternatives.set(item.Id, {
        order,
        sku: match.SKU,
        reserve: match.ReserveCalculated,
        limit: match.LimitCalculated,
        reserveFormula: match.ReserveFormula,
        limitFormula: match.LimitFormula,
        isOriginal: false,
      });
    }
  }

  private effectiveSku(item: DemandItem): string {
    if (!item) return "";
    const chosen = this.chosenAlternatives.get(item.Id);
    return chosen ? chosen.sku : item.SKU;
  }

  private effectiveReserve(item: DemandItem): number | null {
    if (!item) return null;
    const chosen = this.chosenAlternatives.get(item.Id);
    return chosen ? chosen.reserve : item.ReserveCalculated;
  }

  private effectiveLimit(item: DemandItem): number | null {
    if (!item) return null;
    const chosen = this.chosenAlternatives.get(item.Id);
    return chosen ? chosen.limit : item.LimitCalculated;
  }

  private effectiveReserveFormula(item: DemandItem): string {
    if (!item) return "";
    const chosen = this.chosenAlternatives.get(item.Id);
    if (chosen && chosen.reserveFormula !== undefined) return chosen.reserveFormula ?? "";
    return item.ReserveFormula ?? "";
  }

  private effectiveLimitFormula(item: DemandItem): string {
    if (!item) return "";
    const chosen = this.chosenAlternatives.get(item.Id);
    if (chosen && chosen.limitFormula !== undefined) return chosen.limitFormula ?? "";
    return item.LimitFormula ?? "";
  }

  private effectiveExpandedSku(row: DemandItemExpanded): string {
    if (!row) return "";
    const chosen = this.chosenAlternatives.get(row.DemandItemId);
    return chosen ? chosen.sku : row.SKU;
  }

  private effectiveExpandedReserve(row: DemandItemExpanded): number | null {
    if (!row) return null;
    const chosen = this.chosenAlternatives.get(row.DemandItemId);
    if (!chosen) return row.ReserveCalculated;
    const item = this.demandItemRowData.find((i) => i.Id === row.DemandItemId);
    return this.scaleByOriginalShare(row.ReserveCalculated, item?.ReserveCalculated, chosen.reserve, row.DemandItemId);
  }

  private effectiveExpandedLimit(row: DemandItemExpanded): number | null {
    if (!row) return null;
    const chosen = this.chosenAlternatives.get(row.DemandItemId);
    if (!chosen) return row.LimitCalculated;
    const item = this.demandItemRowData.find((i) => i.Id === row.DemandItemId);
    return this.scaleByOriginalShare(row.LimitCalculated, item?.LimitCalculated, chosen.limit, row.DemandItemId);
  }

  /**
   *  Render an empty / null Zone value as "any" so the grid mirrors the backend
   *  normalization (`ZonePlacementOptions.Any` in Lionrock.Bicep.Models).
   */
  private displayZone(zone: string | null | undefined): string {
    return zone && zone.length > 0 ? zone : "any";
  }

  /**
   *  Render a cluster / rack count. Zero means the blueprint did not constrain
   *  that dimension, so it is shown as blank rather than "0".
   */
  private displayPlacementCount(count: number | null | undefined): number | null {
    return typeof count === "number" && count > 0 ? count : null;
  }

  /**
   *  Distribute the chosen-alternative total across the per-zone expanded rows
   *  by preserving each row's share of the original total. If the original
   *  total is zero (or missing), fall back to an even split across rows.
   */
  private scaleByOriginalShare(
    rowOriginal: number,
    itemOriginalTotal: number | null | undefined,
    chosenTotal: number,
    demandItemId: number
  ): number {
    if (itemOriginalTotal && itemOriginalTotal > 0) {
      return Math.round((rowOriginal / itemOriginalTotal) * chosenTotal);
    }
    const siblingCount = this.demandItemExpandedRowData.filter((r) => r.DemandItemId === demandItemId).length || 1;
    return Math.round(chosenTotal / siblingCount);
  }

  // ---------- Alternative selection ----------

  openAlternativeModal(item: DemandItem): void {
    const alternatives = this.alternativesForItem(item.Id);
    if (alternatives.length === 0) {
      return;
    }

    const existing = this.chosenAlternatives.get(item.Id);
    const data: AlternativeSelectionData = {
      demandItemId: item.Id,
      demandItemName: item.Name || `Item ${item.Id}`,
      originalSku: item.SKU || "",
      originalReserve: item.ReserveCalculated ?? 0,
      originalLimit: item.LimitCalculated ?? 0,
      originalReserveFormula: item.ReserveFormula,
      originalLimitFormula: item.LimitFormula,
      alternatives,
      currentOrder: existing ? existing.order : undefined,
    };

    const modalRef = this.modalService.open(AlternativeSelectionModalComponent, {
      backdrop: "static",
      size: "lg",
    });
    modalRef.componentInstance.data = data;

    modalRef.result.then(
      (selected: any) => {
        if (!selected) return;

        if (selected === ALTERNATIVE_SELECTION_CLEARED) {
          this.chosenAlternatives.delete(item.Id);
          this.demandItemGridApi?.refreshCells({ force: true });
          this.cdr.detectChanges();
          this.refreshCalculatedItems();
          return;
        }

        this.chosenAlternatives.set(item.Id, {
          order: selected.isOriginal ? 0 : (selected.order ?? 0),
          sku: selected.sku,
          reserve: selected.reserve,
          limit: selected.limit,
          reserveFormula: selected.reserveFormula,
          limitFormula: selected.limitFormula,
          isOriginal: !!selected.isOriginal,
        });
        this.demandItemGridApi?.refreshCells({ force: true });
        this.cdr.detectChanges();

        // Refresh the calculated demand items + expanded table from the server
        // so the displayed values (SKU/Reserve/Limit, expanded rows, etc.) reflect
        // the newly chosen alternative.
        this.refreshCalculatedItems();
      },
      () => {
        // dismissed - no-op
      }
    );
  }

  /**
   *  Refetches the demand items and the expanded (per-zone) breakdown after an
   *  alternative selection so the grids show server-calculated values.
   */
  private async refreshCalculatedItems(): Promise<void> {
    try {
      const items = await this.apiService.getDemandItemsByDemandId(this.demandId).toPromise();

      this.demandItemRowData = (items ?? []) as DemandItem[];
      this.demandItemExpandedRowData = this.flattenExpandedItems(this.demandItemRowData);

      // Re-apply persisted alternative selections so the UI doesn't snap back
      // to the original SKU after the server round-trip.
      this.hydrateAssignedAlternatives();

      this.demandItemGridApi?.setGridOption("rowData", this.demandItemRowData);
      this.demandItemGridApi?.refreshCells({ force: true });
      this.demandItemExpandedGridApi?.setGridOption("rowData", this.demandItemExpandedRowData);
      this.demandItemExpandedGridApi?.refreshCells({ force: true });
      this.cdr.detectChanges();
    } catch (error: any) {
      this.notificationService.error(
        `Failed to refresh calculated items: ${error?.message || error}`
      );
    }
  }

  // ---------- Sign off / reject ----------

  async signOffAll(): Promise<void> {
    if (!this.canSignoffOrReject) {
      this.notificationService.warning(
        "You do not have permission to sign off this demand, or the demand is not in a state that allows signing off."
      );
      return;
    }

    // Build per-item alternative order map: DemandItemId -> chosen order (0 = original).
    const itemAlternativeOrders: Record<number, number> = {};
    for (const item of this.demandItemRowData) {
      const chosen = this.chosenAlternatives.get(item.Id);
      itemAlternativeOrders[item.Id] = (chosen && !chosen.isOriginal && chosen.order > 0)
        ? chosen.order
        : 0;
    }

    this.loadingService.setLoading(true);
    try {
      await this.apiService.signoffDemand(this.demandId, itemAlternativeOrders).toPromise();
      this.notificationService.success(
        `Signed off ${this.demandItemRowData.length} item(s) successfully!`
      );
      await this.loadAllData();
    } catch (error: any) {
      this.notificationService.error(`Failed to sign off: ${error?.message || error}`);
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  async rejectAll(): Promise<void> {
    if (!this.canSignoffOrReject) {
      this.notificationService.warning(
        "You do not have permission to reject this demand, or the demand is not in a state that allows rejecting."
      );
      return;
    }

    this.loadingService.setLoading(true);
    try {
      await this.apiService.rejectDemand(this.demandId).toPromise();
      this.notificationService.success(
        `Rejected ${this.demandItemRowData.length} item(s) successfully!`
      );
      await this.loadAllData();
    } catch (error: any) {
      this.notificationService.error(
        `Failed to reject: ${error?.message || error?.error?.message || error || "Unknown error"}`
      );
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  @HostListener("window:resize")
  onResize(): void {
    setTimeout(() => {
      this.demandItemGridApi?.autoSizeAllColumns();
      this.demandItemExpandedGridApi?.autoSizeAllColumns();
    }, 100);
  }
}
